import { useState, useEffect } from 'react';
import type { Novel } from '../../services/types';

interface NovelEditorModalProps {
  novel: Novel;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { title?: string; author?: string; description?: string; cover_url?: string }) => Promise<void>;
}

export function NovelEditorModal({ novel, isOpen, onClose, onSave }: NovelEditorModalProps) {
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [description, setDescription] = useState('');
  const [coverUrl, setCoverUrl] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [coverError, setCoverError] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTitle(novel.title || '');
      setAuthor(novel.author || '');
      setDescription(novel.description || '');
      setCoverUrl(novel.cover_url || '');
      setError(null);
      setCoverError(false);
    }
  }, [isOpen, novel]);

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError('請輸入小說標題');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await onSave({
        title: title.trim(),
        author: author.trim() || undefined,
        description: description.trim() || undefined,
        cover_url: coverUrl.trim() || undefined
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : '儲存失敗');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal">
      {/* Backdrop */}
      <div
        className="overlay"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="modal-content">
        {/* Header */}
        <div className="card-header flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-button bg-primary-500 flex items-center justify-center shadow-sm">
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </div>
            <div>
              <h3 className="heading-section">編輯小說資訊</h3>
              <p className="body-small">修改小說的基本資訊</p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="btn btn-ghost btn-icon"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mx-5 mb-4 p-3 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800/50 text-danger-700 dark:text-danger-300 rounded-button flex items-center gap-2">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Form */}
        <div className="card-body space-y-5">
          {/* Cover Preview + Title/Author Row */}
          <div className="flex gap-4">
            {/* Cover Preview */}
            <div className="flex-shrink-0">
              <div className="w-24 h-32 rounded-card overflow-hidden bg-neutral-100 dark:bg-neutral-700 shadow-inner relative group">
                {coverUrl && !coverError ? (
                  <img
                    src={coverUrl}
                    alt="封面預覽"
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    onError={() => setCoverError(true)}
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-neutral-400 dark:text-neutral-500">
                    <svg className="w-8 h-8 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-xs">封面預覽</span>
                  </div>
                )}
              </div>
            </div>

            {/* Title & Author Fields */}
            <div className="flex-1 space-y-3">
              {/* 標題 */}
              <div>
                <label className="label mb-1.5">
                  標題 <span className="text-danger-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="輸入小說標題..."
                  className="input"
                  autoFocus
                />
              </div>

              {/* 作者 */}
              <div>
                <label className="label mb-1.5">作者</label>
                <input
                  type="text"
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  placeholder="小說作者名稱"
                  className="input"
                />
              </div>
            </div>
          </div>

          {/* 封面 URL */}
          <div>
            <label className="label mb-1.5">封面圖片 URL</label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              </div>
              <input
                type="url"
                value={coverUrl}
                onChange={(e) => {
                  setCoverUrl(e.target.value);
                  setCoverError(false);
                }}
                placeholder="https://example.com/cover.jpg"
                className="input pl-10"
              />
            </div>
            {coverError && coverUrl && (
              <p className="mt-1.5 text-xs text-warning-600 dark:text-warning-400 flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                圖片載入失敗，請確認網址是否正確
              </p>
            )}
          </div>

          {/* 簡介 */}
          <div>
            <label className="label mb-1.5">簡介</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="小說的簡介與內容描述..."
              rows={4}
              className="textarea"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="card-footer flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="btn btn-ghost btn-md"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={!title.trim() || isSaving}
            className="btn btn-primary btn-md"
          >
            {isSaving ? (
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                儲存中...
              </span>
            ) : (
              <span className="flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                儲存變更
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
